/** Drives camera shake, red flash overlays, and fade-to-black effects for combat feedback. */
import Phaser from 'phaser';
import { ConfigLoader } from './ConfigLoader';
import { GAME_WIDTH, GAME_HEIGHT, SF0_X, SF0_Y } from '../utils/Constants';

/** Wraps Phaser camera and overlay APIs to deliver tuned combat screen-feedback effects. */
export class ScreenEffects {
  private camera: Phaser.Cameras.Scene2D.Camera;
  private scene: Phaser.Scene;
  private redOverlay: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.camera = scene.cameras.main;
  }

  /** Shake the camera by the given pixel intensity for the given duration. */
  shake(intensity: number, durationMs: number): void {
    // Phaser shake intensity is 0-1 range, our config is in pixels
    // Convert pixel intensity to fraction of game width
    const normalizedIntensity = intensity / GAME_WIDTH;
    this.camera.shake(durationMs, normalizedIntensity);
  }

  /** Trigger shake and red flash when the player takes damage. */
  playerHitEffect(): void {
    const config = ConfigLoader.combat.screenEffects.playerHit;
    this.shake(config.shakeIntensity, config.shakeDurationMs);
    this.redFlash(config.redFlashAlpha, config.redFlashDurationMs);
  }

  /** Trigger camera shake when the player lands a hit on an enemy. */
  heavyHitEffect(): void {
    const config = ConfigLoader.combat.screenEffects.heavyHitLanded;
    this.shake(config.shakeIntensity, config.shakeDurationMs);
  }

  private redFlash(alpha: number, durationMs: number): void {
    if (this.redOverlay) {
      this.redOverlay.destroy();
    }

    this.redOverlay = this.scene.add.rectangle(
      SF0_X + GAME_WIDTH / 2, SF0_Y + GAME_HEIGHT / 2,
      GAME_WIDTH, GAME_HEIGHT,
      0xff0000, alpha
    );
    this.redOverlay.setScrollFactor(0);
    this.redOverlay.setDepth(1000);

    this.scene.tweens.add({
      targets: this.redOverlay,
      alpha: 0,
      duration: durationMs,
      onComplete: () => {
        this.redOverlay?.destroy();
        this.redOverlay = null;
      },
    });
  }

  /** Clean up any active overlay objects. */
  destroy(): void {
    this.redOverlay?.destroy();
  }
}
